import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { fetchInventoryRegistries, deleteInventoryRegistry, exportInventoryRegistries, acceptTransfer, rejectTransfer } from '../services/inventoryRegistries.services';
import { inventoryRegistriesColumns, columnsExcel } from '../utils/inventoryRegistriesColumns';
import { fetchProducts } from '@/pages/parametrizacion/products/Services/products.services';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, XCircle, Loader2, ArrowRightLeft } from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const estadoBadge = (estado) => {
  if (!estado) return null;
  const map = {
    pendiente: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pendiente' },
    aceptado: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Aceptado' },
    rechazado: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Rechazado' },
  };
  const s = map[estado] || { bg: 'bg-gray-100', text: 'text-gray-800', label: estado };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const TrasladosTable = ({ idLavanderia, refreshKey = 0, onTransferAction, onOpenTransferModal }) => {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // grupoTrasladoId being processed
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const baseMovs = ['trasladoSalida', 'trasladoEntrada', 'entrada'];
      const query = { idLavanderia, tipoMovimiento: { $in: baseMovs }, ...filters };
      const { data: rows, totalRecords } = await fetchInventoryRegistries({ limit: ITEMS_PER_PAGE, offset: currentPage, query });
      const normalized = (rows || []).map(r => ({
        ...r,
        // Mostrar cantidadInicial como 'cantidad' si falta el campo cantidad
        cantidad: (r?.cantidad === undefined || r?.cantidad === null)
          ? r?.cantidadInicial
          : r?.cantidad,
        // Unificar campo de observación
        observacion: r?.observacion ?? r?.observaciones ?? r?.observacion,
      }));
      setData(normalized);
      setTotalItems(totalRecords);
    } catch (e) {
      console.error('Error cargando traslados:', e);
      toast({ title: 'Error', description: 'No se pudieron cargar los traslados.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [idLavanderia, currentPage, filters, toast]);

  useEffect(() => { load(); }, [load, filters, currentPage, refreshKey]);

  // Load products for filter options
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await fetchProducts({ limit: 500, offset: 1, query: {} });
        setProducts(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  const handleAccept = async (grupoTrasladoId) => {
    if (!grupoTrasladoId) return;
    const ok = window.confirm('¿Deseas aceptar este traslado? Se ajustará el inventario de ambos centros.');
    if (!ok) return;
    try {
      setActionLoading(grupoTrasladoId);
      const res = await acceptTransfer(grupoTrasladoId);
      toast({ title: 'Traslado aceptado', description: res?.message || 'El traslado fue aceptado correctamente.' });
      await load();
      onTransferAction && onTransferAction();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo aceptar el traslado.', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (grupoTrasladoId) => {
    if (!grupoTrasladoId) return;
    const ok = window.confirm('¿Deseas rechazar este traslado? Los registros se marcarán como rechazados y no se ajustará inventario.');
    if (!ok) return;
    try {
      setActionLoading(grupoTrasladoId);
      const res = await rejectTransfer(grupoTrasladoId);
      toast({ title: 'Traslado rechazado', description: res?.message || 'El traslado fue rechazado.' });
      await load();
      onTransferAction && onTransferAction();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo rechazar el traslado.', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Columnas relevantes para traslados
  const baseColumns = inventoryRegistriesColumns.filter(c =>
    ['fecha', 'producto', 'tipoMovimiento', 'cantidad', 'lavanderiaDestino', 'observacion', 'createdAt'].includes(c.id)
  );

  // Agregar columna de lavanderiaOrigen
  const lavanderiaOrigenCol = {
    id: 'lavanderiaOrigen',
    label: 'Lavandería Origen',
    type: 'text',
    sortable: false,
    render: (row) => {
      const value = row?.lavanderiaOrigen;
      if (!value) return '-';
      return typeof value === 'object' ? value.nombre || '-' : value;
    }
  };

  // Agregar columna de estado del traslado
  const estadoTrasladoCol = {
    id: 'estadoTraslado',
    label: 'Estado Traslado',
    type: 'text',
    sortable: true,
    render: (row) => estadoBadge(row?.estadoTraslado),
  };

  // Columna de acciones personalizada con render() — DataTable ahora soporta render en la columna opciones
  const actionsColumn = {
    id: 'opciones',
    key: 'opciones',
    label: 'Opciones',
    render: (row) => {
      const isPendingEntrada = row?.tipoMovimiento === 'trasladoEntrada' && row?.estadoTraslado === 'pendiente' && row?.grupoTrasladoId;
      const isProcessing = actionLoading === row?.grupoTrasladoId;
      return (
        <>
          {isPendingEntrada && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                title="Aceptar traslado"
                onClick={() => handleAccept(row.grupoTrasladoId)}
                disabled={isProcessing}
              >
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Rechazar traslado"
                onClick={() => handleReject(row.grupoTrasladoId)}
                disabled={isProcessing}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </>
          )}
        </>
      );
    },
  };

  const columns = [actionsColumn, ...baseColumns, lavanderiaOrigenCol, estadoTrasladoCol];

  const handleAction = async (actionKey, row) => {
    if (actionKey === 'delete') {
      if (!isAdmin()) return;
      const ok = window.confirm('¿Deseas eliminar este registro? Esta acción no se puede deshacer.');
      if (!ok) return;
      try {
        await deleteInventoryRegistry(row._id || row.id);
        toast({ title: 'Eliminado', description: 'Registro eliminado correctamente.' });
        await load();
      } catch (e) {
        toast({ title: 'Error', description: 'No se pudo eliminar el registro.', variant: 'destructive' });
      }
    }
  };

  const productOptions = products.map(p => ({ value: p._id || p.id, label: p.nombre }));

  const filterFields = [
    { id: 'fecha', label: 'Rango de fechas', type: 'daterange' },
    { id: 'producto.id', label: 'Producto', type: 'select', options: [{ value: '', label: '-- Todos --' }, ...productOptions] },
    { id: 'tipoMovimiento', label: 'Tipo movimiento', type: 'select', options: [
      { value: '', label: '-- Todos --' },
      { value: 'trasladoSalida', label: 'Traslado Salida' },
      { value: 'trasladoEntrada', label: 'Traslado Entrada' },
      { value: 'entrada', label: 'Entrada' },
    ] },
    { id: 'estadoTraslado', label: 'Estado traslado', type: 'select', options: [
      { value: '', label: '-- Todos --' },
      { value: 'pendiente', label: 'Pendiente' },
      { value: 'aceptado', label: 'Aceptado' },
      { value: 'rechazado', label: 'Rechazado' },
    ] },
  ];

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const handleRemoveFilter = (key) => {
    const nf = { ...filters };
    delete nf[key];
    setFilters(nf);
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">Tabla de Traslados</CardTitle>
            <CardDescription>Movimientos de traslado entre centros</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {onOpenTransferModal && (
              <Button size="sm" onClick={onOpenTransferModal}>
                <ArrowRightLeft className="h-4 w-4 mr-1" /> Crear traslado
              </Button>
            )}
            <ExportExcelButton
              fileName="Traslados"
              columns={columnsExcel}
              filters={{ "lavanderia.id": idLavanderia, tipoMovimiento: { $in: ['trasladoSalida','trasladoEntrada','entrada'] }, ...filters }}
              service={exportInventoryRegistries}
            />
            <Button variant="outline" size="sm" onClick={() => setIsDrawerOpen(true)} disabled={loadingProducts}>
              {loadingProducts ? 'Cargando…' : 'Filtros'}
            </Button>
          </div>
        </div>
        <div className="mt-2">
          <AppliedFilters filters={filters} fields={filterFields} onRemoveFilter={handleRemoveFilter} />
        </div>
      </CardHeader>
      <CardContent>
        <DataTable data={data} columns={columns} isLoading={loading} showCreateButton={false} onAction={handleAction} />
        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </CardContent>
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onChange={handleFilterApply}
        onApply={handleFilterApply}
        initialDay={false}
      />
    </Card>
  );
};

export default TrasladosTable;
